import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { frameworkSteps } from '../mock';

export const Framework3D = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerRef = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);

  return (
    <section ref={containerRef} className="relative bg-dark py-24 md:py-32 px-6 overflow-hidden">
      {/* Animated Background Elements */}
      <motion.div
        className="absolute top-1/4 left-0 w-64 h-64 border border-[#c6a04a]/10 rounded-full"
        style={{ rotate, y }}
      />
      <motion.div
        className="absolute bottom-1/4 right-0 w-96 h-96 border border-[#d9be7e]/10 rounded-full"
        style={{ rotate: useTransform(rotate, r => -r), y: useTransform(y, val => -val) }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div 
          ref={ref}
          className="text-center mb-20 space-y-6"
          initial={{ opacity: 0, y: 100 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="font-accent text-xl md:text-2xl italic text-gold"
            initial={{ opacity: 0, scale: 0 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            The CT3 Performance Framework
          </motion.p>
          <motion.h2 
            className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide leading-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            style={{ fontWeight: 700 }}
          >
            <span className="text-cream">One System.</span>
            <br />
            <span className="text-gold">Four Layers.</span>
            <br />
            <span className="text-cream">Zero Guesswork.</span>
          </motion.h2>
        </motion.div>

        {/* 3D Step Cards */}
        <div className="space-y-12">
          {frameworkSteps.map((step, index) => (
            <FrameworkStep3D key={index} step={step} index={index} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const FrameworkStep3D = ({ step, index, inView }) => {
  const wrapperRef = React.useRef(null);
  const [cardInView, setCardInView] = React.useState(false);

  React.useEffect(() => {
    if (!wrapperRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const containerRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 30;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -30;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  const isEven = index % 2 === 0;

  return (
    <div ref={wrapperRef}>
    <motion.div
      initial={{ 
        opacity: 0, 
        x: isEven ? -80 : 80,
        rotateY: isEven ? -30 : 30
      }}
      animate={cardInView ? { 
        opacity: 1, 
        x: 0,
        rotateY: 0
      } : {}}
      transition={{ 
        delay: index * 0.1,
        duration: 0.8,
        type: "spring",
        stiffness: 70
      }}
      style={{ perspective: '2000px' }}
    >
      <motion.div
        ref={containerRef}
        className={`grid md:grid-cols-12 gap-8 items-center cursor-pointer ${isEven ? '' : 'md:dir-rtl'}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        animate={{
          rotateX: mousePosition.y * 0.3,
          rotateY: mousePosition.x * 0.3,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Number Badge */}
        <motion.div 
          className="md:col-span-3 flex justify-center md:justify-start"
          style={{ transform: 'translateZ(80px)' }}
        >
          <motion.div
            className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center"
            whileHover={{ scale: 1.1, rotate: 360 }}
            transition={{ duration: 0.6 }}
          >
            <div className="absolute inset-0 border-4 border-[#c6a04a]/30 transform rotate-45" />
            <div className="absolute inset-4 border-2 border-[#d9be7e]/30 transform rotate-45" />
            <motion.p 
              className="font-display text-6xl md:text-7xl text-gold relative z-10"
              animate={{
                textShadow: [
                  '0 0 20px rgba(198, 160, 74, 0.5)',
                  '0 0 40px rgba(198, 160, 74, 0.8)',
                  '0 0 20px rgba(198, 160, 74, 0.5)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {step.number}
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Content Card */}
        <motion.div 
          className="md:col-span-9 bg-panel p-8 md:p-10 border-2 border-[#c6a04a]/30 relative overflow-hidden group"
          style={{ transform: 'translateZ(50px)' }}
          whileHover={{ 
            borderColor: 'rgba(198, 160, 74, 0.8)',
            boxShadow: '0 30px 80px rgba(198, 160, 74, 0.3)'
          }}
        >
          {/* Animated Background */}
          <motion.div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(198, 160, 74, 0.1), transparent 70%)'
            }}
          />

          {/* Content */}
          <div className="relative z-10">
            <motion.h3 
              className="font-display text-2xl md:text-4xl uppercase tracking-tight text-gold mb-4"
              whileHover={{ x: 10, transition: { duration: 0.2 } }}
            >
              {step.title}
            </motion.h3>
            <p className="font-body text-base md:text-lg text-[#e8e6df] leading-relaxed font-light">
              {step.description}
            </p>
          </div>

          {/* 3D Corner Accents */}
          <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-gold/50 group-hover:border-gold transition-colors duration-300" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-gold/50 group-hover:border-gold transition-colors duration-300" />
        </motion.div>
      </motion.div>
    </motion.div>
    </div>
  );
};
